import React, { useRef, useState } from 'react';
import appNoticeService from '../../services/appNoticeService';

/**
 * ONE image picker for the sheet hero and the push image (design_handoff_app_notices
 * "LOOK › IMAGE" / "PUSH › IMAGE"): 72×56 radius-14 tiles — recent uploads first, then the
 * library (images already on other sheets / pushes) — plus a dashed `+ UPLOAD` tile. An
 * upload goes through the notices image endpoint (WebP, 1200px, `notices/`), is selected
 * the moment it lands, and is remembered in `recent` for the session.
 *
 * value = absolute URL (or ''); onChange(url, path). `optional` adds the NONE tile.
 */
const RING_ON = '0 0 0 2px #fff, 0 0 0 4px #0A0A0A';

export default function ImageField({ value, onChange, optional = false, library = [], recent = [], onUploaded, help, disabled = false, testId = 'image-field' }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    const r = await appNoticeService.uploadImage(file);
    setBusy(false);
    if (r.success && r.data?.url) {
      onUploaded?.(r.data.url, r.data.path);
      onChange(r.data.url, r.data.path);
    } else setError(r.error || 'Could not upload that image');
  };

  const tiles = [];
  const seen = new Set();
  [...recent, ...library].forEach((u) => { if (u && !seen.has(u)) { seen.add(u); tiles.push(u); } });
  if (value && !seen.has(value)) tiles.unshift(value);

  const tile = 'flex-shrink-0 cursor-pointer border-0 p-0 disabled:cursor-not-allowed';
  return (
    <div className="flex flex-col" style={{ gap: 10 }} data-testid={testId}>
      <div className="flex flex-wrap" style={{ gap: 8, padding: 14, borderRadius: 22, background: '#F5F5F5' }}>
        {optional && (
          <button type="button" disabled={disabled} onClick={() => onChange('', null)} aria-pressed={!value} className={`${tile} font-mono text-[9px] font-extrabold tracking-[0.1em]`}
            style={{ width: 72, height: 56, borderRadius: 14, background: '#F2F2F2', boxShadow: !value ? 'inset 0 0 0 2px #0A0A0A' : 'none' }}>NONE</button>
        )}
        {tiles.map((u) => (
          <button key={u} type="button" disabled={disabled} onClick={() => onChange(u, null)} aria-pressed={value === u} aria-label="Use this image" className={tile}
            style={{ width: 72, height: 56, borderRadius: 14, background: `#EDEDED url(${u}) center/cover no-repeat`, boxShadow: value === u ? RING_ON : 'none' }} />
        ))}
        <button type="button" disabled={busy || disabled} onClick={() => fileRef.current?.click()} className={`${tile} font-mono text-[9px] font-extrabold tracking-[0.1em] text-[#6B7280] hover:border-black hover:text-black`}
          style={{ width: 72, height: 56, border: '2px dashed #CFCFCF', borderRadius: 14, background: '#fff' }}>{busy ? '…' : '+ UPLOAD'}</button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" aria-label="Upload an image" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
        {help && <span className="font-mono text-[10px] text-[#6B7280]" style={{ flex: '1 1 100%' }}>{help}</span>}
      </div>
      {error && <div className="font-mono text-[11px] text-[#DC2626]">{error}</div>}
    </div>
  );
}
